/*
 *  Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { Text, theme } from "@nordcloud/gnui";
import { DateRange } from "~/components";
import { EstatePeriod } from "~/components/DateRangeSelector";
import { dateFormat } from "~/constants";
import { useBillingPeriod } from "~/services/customers";
import { isNotNil } from "~/tools";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
};

export function PeriodSelection({ period, range }: Props) {
  const { isPreviousBillingPeriod } = useBillingPeriod();
  return (
    <Text
      weight="medium"
      ml={theme.spacing.spacing03}
      my={theme.spacing.spacing04}
      data-testid="period-selection"
    >
      {getSelectedDates(period, isPreviousBillingPeriod, range)}
    </Text>
  );
}

function getSelectedDates(
  period: EstatePeriod,
  isPreviousBillingPeriod: boolean,
  range?: DateRange
) {
  if (period === EstatePeriod.RANGE && isNotNil(range)) {
    return getRangeDates(range);
  }

  return getMonthDates(period, isPreviousBillingPeriod);
}

function getMonthDates(period: EstatePeriod, isPreviousBillingPeriod: boolean) {
  const defaultDay = isPreviousBillingPeriod
    ? dayjs().subtract(1, "month").endOf("month")
    : dayjs();

  if (period === EstatePeriod.PREVIOUS_MONTH) {
    const previousMonth = defaultDay.add(-1, "month").endOf("month").toString();

    return `1 - ${formatDate(previousMonth)}`;
  }

  return `1 - ${formatDate(defaultDay.toString())}`;
}

function getRangeDates(range: DateRange) {
  const { from, to } = range;
  return `${formatDate(from)} - ${formatDate(to)}`;
}

function formatDate(date: string) {
  return `${dayjs(date).format(dateFormat.dayShort)} ${dayjs(date).format(
    dateFormat.monthName
  )} ${dayjs(date).format(dateFormat.year)}`;
}
